import { Box, CircularProgress, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AttachmentInput } from '~/components/AttachmentInput';
import { CurrencyField } from '~/components/GenericForm/CurrencyField';
import { EnumSelect } from '~/components/GenericForm/EnumSelect';
import { BenefitProposalProps } from '~/data/proposals';
import {
  CommonEmployeeProposalFormHandle,
  CommonEmployeeProposalFormProps,
  ProposalDataFormButtons,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { ProposalResponsibleHRSelect } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/components/ProposalResponsibleHrSelect';
import { useProposalFormImperativeHandle } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useProposalFormImperativeHandle';
import { useEmployeeDataForProposal } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/useEmployeeDataForProposal';
import { convertDateTypeDatePickerProps } from '~/utils/helpers/date';

import { ScheduleEmploymentsDropdown } from '../Common/components/ScheduleEmploymentsDropdown';
import { useSxStyles } from '../Common/useStyles';

import { useBenefitProposalForm } from './useBenefitProposalForm';

type PropsType = CommonEmployeeProposalFormProps & {
  proposal: BenefitProposalProps;
};

export const BenefitsProposalForm = React.forwardRef<
  CommonEmployeeProposalFormHandle,
  PropsType
>((props, ref) => {
  const { proposal, employeeId, isNewProposal, onProposalUpdate } = props;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { currency } = useEmployeeDataForProposal({ proposal });
  const {
    form,
    enums: { benefitTypes, eventFrequencies },
    responsibleHRSelector,
    handleFormSubmit,
    handleFormReset,
    readOnlyMode,
    canSubmitForm,
    isLoading,
    fieldProps,
    attachmentUrl,
  } = useBenefitProposalForm({ proposal, employeeId, onProposalUpdate });
  const { control, formState, setValue } = form;

  useProposalFormImperativeHandle({ ref, handleFormSubmit });

  if (isLoading.responsibleHR || isLoading.commonFieldProps) {
    return <CircularProgress />;
  }

  return (
    <Box autoComplete="off" component="form" sx={sxStyles.form}>
      <Controller
        name={'employmentId'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <ScheduleEmploymentsDropdown
              value={value}
              onChange={onChange}
              error={error}
              readOnlyMode={readOnlyMode}
            />
          );
        }}
      />
      <Controller
        name={'benefitType'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <EnumSelect
              FormControlProps={{ fullWidth: true, error: !!error?.message }}
              value={value}
              onChange={onChange}
              enumOptions={benefitTypes}
              id={'benefitType'}
              label={t('laBenefitType')}
              required={true}
              error={!!error?.message}
              helperText={error?.message}
              disabled={readOnlyMode}
            />
          );
        }}
      />
      <Controller
        name={'benefitFrequency'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <EnumSelect
              FormControlProps={{ fullWidth: true, error: !!error?.message }}
              value={value}
              onChange={onChange}
              enumOptions={eventFrequencies}
              id={'frequency'}
              label={t('laFrequency')}
              required={true}
              error={!!error?.message}
              helperText={error?.message}
              disabled={readOnlyMode}
            />
          );
        }}
      />
      <Box sx={sxStyles.dateRange}>
        <Controller
          name={'startDate'}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <DatePicker
                label={t('laStartDate')}
                {...convertDateTypeDatePickerProps({
                  value,
                  onChange,
                  ...fieldProps.datePickers.startDate,
                })}
                disabled={readOnlyMode}
                slotProps={{
                  textField: {
                    required: true,
                    error: !!error?.message,
                    helperText: error?.message,
                  },
                }}
              />
            );
          }}
        />
        <Box sx={{ pb: 3 }}>-</Box>
        <Controller
          name={'endDate'}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <DatePicker
                label={t('laEndDate')}
                {...convertDateTypeDatePickerProps({
                  value,
                  onChange,
                  ...fieldProps.datePickers.endDate,
                })}
                disabled={readOnlyMode}
                slotProps={{
                  textField: {
                    error: !!error?.message,
                    helperText: error?.message,
                  },
                }}
              />
            );
          }}
        />
      </Box>
      <Box sx={sxStyles.fieldRow}>
        <Controller
          name={'benefitAmount'}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <CurrencyField
                label={t('laBenefit')}
                value={value}
                onChange={onChange}
                currency={currency}
                error={!!error?.message}
                helperText={error?.message}
                disabled={readOnlyMode}
              />
            );
          }}
        />
        <Controller
          name={'taxableAmount'}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <CurrencyField
                label={t('laTaxable')}
                value={value}
                onChange={onChange}
                currency={currency}
                error={!!error?.message}
                helperText={error?.message}
                disabled={readOnlyMode}
              />
            );
          }}
        />
      </Box>
      <Controller
        name={'info'}
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <TextField
              label={t('laBenefitInfo')}
              {...field}
              fullWidth={true}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
      <Controller
        name={'attachmentName'}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <AttachmentInput
              onChange={(selectedFile?: File) => {
                onChange(selectedFile?.name || '');
                setValue('attachment', selectedFile || null);
                setValue('attachmentId', -1);
              }}
              value={value}
              attachmentUrl={attachmentUrl}
              fullWidth={true}
              label={t('attachment')}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
      <ProposalResponsibleHRSelect
        form={form}
        control={control}
        selectorSettings={responsibleHRSelector}
        disabled={readOnlyMode}
      />
      <ProposalDataFormButtons
        isNewProposal={isNewProposal}
        onFormSubmitWithDecision={handleFormSubmit}
        onFormReset={handleFormReset}
        formState={formState}
        readOnlyMode={readOnlyMode}
        submitDecisionAllowed={canSubmitForm}
      />
    </Box>
  );
});
